import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  eventReorderScreenDone,
} from 'store/actions';
import {
  doSetupEngageElementUpdate,
  eventSetupEngageCheckUpdated,
} from 'store/remote/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual, orderBy } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { CardRow } from 'components/CardRow';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { EngageSettingsActions } from './EngageSettingsActions';

export const EngageSettingsElementModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsEngageElement>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const eventKeyFieldsRef = React.useRef(randomKey());
  const eventKeyChecksRef = React.useRef(randomKey());

  const { element } = route.params;

  const dataRef = React.useRef(cloneDeep(element));
  const isDirty = React.useCallback(
    () => !isEqual(element, dataRef.current),
    [element],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const fields = orderBy(dataRef.current.fields, [
    'ordering',
    'defaultOrdering',
  ]);

  const checks = orderBy(dataRef.current.checks, ['ordering', 'name']);

  const handleSave = React.useCallback(
    () => dispatch(doSetupEngageElementUpdate({ element: dataRef.current })),
    [dispatch],
  );

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  const handleToggleField = (fieldId: string) => {
    const field = dataRef.current.fields.find((f) => f.id === fieldId);
    if (field) {
      field.enable = !field.enable;
      forceUpdate();
    }
  };

  const handleToggleCheck = (checkId: string) => {
    const check = dataRef.current.checks.find((c) => c.id === checkId);
    if (check) {
      check.enable = !check.enable;
      forceUpdate();
    }
  };

  React.useEffect(() => {
    const events = [
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === eventKeyFieldsRef.current) {
          const newOrder = action.payload.stringValues.reduce<
            Record<string, number>
          >((acc, fieldId, index) => {
            acc[fieldId] = index + 1;
            return acc;
          }, {});
          dataRef.current.fields.forEach((field, index) => {
            dataRef.current.fields[index]!.ordering = newOrder[field.id]!;
          });
          forceUpdate();
        } else if (action.payload.key === eventKeyChecksRef.current) {
          const newOrder = action.payload.stringValues.reduce<
            Record<string, number>
          >((acc, checkId, index) => {
            acc[checkId] = index + 1;
            return acc;
          }, {});
          dataRef.current.checks.forEach((check, index) => {
            dataRef.current.checks[index]!.ordering = newOrder[check.id]!;
          });
          forceUpdate();
        }
      }),
      doAddEvent(eventSetupEngageCheckUpdated, (action) => {
        if (action.payload.eventKey === eventKeyChecksRef.current) {
          const index = dataRef.current.checks.findIndex(
            (c) => c.id === action.payload.check.id,
          );
          if (index !== -1) {
            dataRef.current.checks[index] = {
              ...dataRef.current.checks[index],
              ...action.payload.check,
            };
            forceUpdate();
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Engage Element"
          subheading={`${element.name} (${element.sectionName})`}
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Details & Listing Page">
        <CardRow
          label="Details Page"
          value={`Each ${element.nameSingular.toLowerCase()} will have its own details page`}
          labelAlignTop
          checkedValue={dataRef.current.enablePage}
          onPress={() =>
            handleChangeValue('enablePage', !dataRef.current.enablePage, true)
          }
        />
        <CardRow
          label="Listing Page"
          value={`Each ${element.nameSingular.toLowerCase()} will be shown on a listing page`}
          labelAlignTop
          checkedValue={dataRef.current.enableListing}
          onPress={() =>
            handleChangeValue(
              'enableListing',
              !dataRef.current.enableListing,
              true,
            )
          }
        />
      </Card>

      <Card label="Asset Page" marginBottomQuarter>
        <CardRow
          label="Show on Asset Page"
          value={`Show a summary of ${element.name.toLowerCase()} on the asset page`}
          labelAlignTop
          checkedValue={dataRef.current.enableAsset}
          onPress={() =>
            handleChangeValue('enableAsset', !dataRef.current.enableAsset, true)
          }
        />
      </Card>
      <Card>
        <CardRow
          label={`Show Total Number of ${element.name}`}
          labelAlignTop
          checkedValue={dataRef.current.assetShowTotal}
          onPress={() =>
            handleChangeValue(
              'assetShowTotal',
              !dataRef.current.assetShowTotal,
              true,
            )
          }
        />
        <CardRow
          label={`Show When No ${element.name} Exist`}
          labelAlignTop
          checkedValue={dataRef.current.assetShowWhenNone}
          onPress={() =>
            handleChangeValue(
              'assetShowWhenNone',
              !dataRef.current.assetShowWhenNone,
              true,
            )
          }
        />
      </Card>

      <Card label="Description">
        <TextAreaField
          placeholder="No Description"
          onChange={(value) => handleChangeValue('description', value)}
          value={dataRef.current.description}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>

      {!!fields.length && !!checks.length && (
        <SeparatorHorizontal withMarginBottom />
      )}

      <EngageSettingsActions
        label="Actions"
        explanation="Actions are shown on the details page. The total number of actions also appear on the asset and listing page"
        onChange={(key, value) => handleChangeValue(key, value, true)}
        enableTasks={dataRef.current.enableTasks}
        enableTaskDueDate={dataRef.current.enableTaskDueDate}
        enableTaskPhotos={dataRef.current.enableTaskPhotos}
        enableTaskSeverity={dataRef.current.enableTaskSeverity}
        enableTaskTimescale={dataRef.current.enableTaskTimescale}
        enableTaskCategory={dataRef.current.enableTaskCategory}
      />

      {!!fields.length && (
        <Card
          label="Fields"
          explanation={smartText(
            'Fields are shown on the details page. ' +
              'Some location related fields may also appear on the listing page',
          )}
          labelLinkText="Change Order"
          onLabelLinkPress={
            fields.length < 2
              ? null
              : () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        options: fields.map((f) => ({
                          name: f.name,
                          value: f.id,
                          valueStyle: !f.enable
                            ? {
                                textDecorationLine: 'line-through',
                                color: theme.colors.placeholder,
                              }
                            : undefined,
                        })),
                        eventKey: eventKeyFieldsRef.current,
                      },
                    }),
                  )
          }
        >
          {fields.map((field) => (
            <CardRow
              key={field.id}
              label={field.name}
              checkedValue={field.enable}
              onPress={() => handleToggleField(field.id)}
            />
          ))}
        </Card>
      )}

      {!!checks.length && (
        <Card
          label="Checks"
          explanation={smartText(
            'Checks are shown on the details pages. A summary also appears on the asset page',
          )}
          labelLinkText="Change Order"
          onLabelLinkPress={
            checks.length < 2
              ? null
              : () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        options: checks.map((f) => ({
                          name: f.name,
                          value: f.id,
                          valueStyle: !f.enable
                            ? {
                                textDecorationLine: 'line-through',
                                color: theme.colors.placeholder,
                              }
                            : undefined,
                        })),
                        eventKey: eventKeyChecksRef.current,
                      },
                    }),
                  )
          }
        >
          {checks.map((check) => (
            <CardRow
              key={check.id}
              label={check.name}
              label2={check.description}
              checkedValue={check.enable}
              onIconPress={() => handleToggleCheck(check.id)}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SettingsEngageCheck,
                    params: {
                      eventKey: eventKeyChecksRef.current,
                      check: { ...check },
                    },
                  }),
                )
              }
            />
          ))}
        </Card>
      )}
    </PageScrollView>
  );
};
